import { useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useReducedMotion } from '@/lib/useReducedMotion'
import { useTheme } from './useTheme'

// ─── Icon components ──────────────────────────────────────────────────────────

function MoonIcon() {
  return (
    <svg viewBox="0 0 24 24" className="w-5 h-5" fill="currentColor" aria-hidden="true">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  )
}

function SunIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      className="w-5 h-5"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <circle cx="12" cy="12" r="4" fill="currentColor" />
      <line x1="12" y1="2" x2="12" y2="5" />
      <line x1="12" y1="19" x2="12" y2="22" />
      <line x1="2" y1="12" x2="5" y2="12" />
      <line x1="19" y1="12" x2="22" y2="12" />
      <line x1="4.93" y1="4.93" x2="7.05" y2="7.05" />
      <line x1="16.95" y1="16.95" x2="19.07" y2="19.07" />
      <line x1="4.93" y1="19.07" x2="7.05" y2="16.95" />
      <line x1="16.95" y1="7.05" x2="19.07" y2="4.93" />
    </svg>
  )
}

// ─── ThemeToggle ──────────────────────────────────────────────────────────────

export function ThemeToggle() {
  const { theme, setTheme } = useTheme()
  const { t } = useTranslation()
  const reduced = useReducedMotion()
  const [hover, setHover] = useState(false)
  const [wiping, setWiping] = useState(false)
  const buttonRef = useRef<HTMLButtonElement | null>(null)

  const isDark = theme === 'dark'
  const next = isDark ? 'light' : 'dark'

  // Destination palette colours:
  //   light mode → destination dark  → fill bg: mono-900 (#161616), icon: mono-200
  //   dark  mode → destination light → fill bg: mono-200 (#e0e0e0), icon: mono-900
  const fillBg = isDark ? 'bg-mono-200' : 'bg-mono-900'
  const iconHoverColor = isDark ? 'text-mono-900' : 'text-mono-200'

  function handleClick() {
    if (reduced) {
      setTheme(next)
      return
    }

    // Set wipe origin to the button's centre
    const rect = buttonRef.current?.getBoundingClientRect()
    const cx = rect ? rect.left + rect.width / 2 : window.innerWidth / 2
    const cy = rect ? rect.top + rect.height / 2 : 0
    document.documentElement.style.setProperty('--wipe-x', `${cx}px`)
    document.documentElement.style.setProperty('--wipe-y', `${cy}px`)

    setWiping(true)
    // After the ~300 ms full-viewport wipe, swap theme then unmount overlay
    setTimeout(() => {
      setTheme(next)
      setTimeout(() => setWiping(false), 30)
    }, 300)
  }

  const showRadial = hover && !reduced

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        aria-label={t('theme.toggle')}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        onClick={handleClick}
        className="relative overflow-hidden p-2 rounded-md transition-colors text-mono-700 dark:text-mono-200"
      >
        {/* Radial fill — expands from centre toward edges on hover */}
        <span
          aria-hidden="true"
          className={[
            'pointer-events-none absolute inset-0 rounded-md',
            'transition-transform duration-200 ease-out',
            fillBg,
            showRadial ? 'scale-150' : 'scale-0',
          ].join(' ')}
          style={{ transformOrigin: 'center' }}
        />

        {/* Icon — sits above the fill; colour flips when fill covers it */}
        <span className={`relative ${showRadial ? iconHoverColor : ''}`}>
          {isDark ? <SunIcon /> : <MoonIcon />}
        </span>
      </button>

      {/* Full-viewport wipe overlay (portal) — only during click animation */}
      {wiping &&
        createPortal(
          <div
            aria-hidden="true"
            className={`fixed inset-0 z-[9999] pointer-events-none ${isDark ? 'bg-mono-200' : 'bg-mono-900'}`}
            style={{ animation: 'theme-wipe 300ms ease-out forwards' }}
          />,
          document.body,
        )}

      {/*
       * Keyframes for the wipe animation.
       * --wipe-x / --wipe-y are set on <html> from the button's bounding rect.
       * Fallback: top-right corner (where the toggle typically lives).
       */}
      <style>{`
        @keyframes theme-wipe {
          0%   { clip-path: circle(0%   at var(--wipe-x, 100%) var(--wipe-y, 0%)); }
          100% { clip-path: circle(150% at var(--wipe-x, 100%) var(--wipe-y, 0%)); }
        }
      `}</style>
    </>
  )
}
